/**
 * Lifecycle tables for setup-step runs, setup-graph nodes, run-full-setup drives, Play store
 * submissions and EAS builds.
 *
 * Imports `./setup-last-run` and `./setup-graph` directly, never `./index`: `./index` re-exports
 * this file, so a runtime import of it would be a cycle. The store-submission status is defined in
 * `./index` itself and is taken with `import type`, which is erased at build time.
 *
 * Plan: docs/plans/2026-10-01-2026-refactor-web-04-run-lifecycle-module-plan.md (U2)
 */

import type { z } from 'zod'
import { defineRunLifecycle, type RunPhase } from '../../schemas/entities/run-lifecycle'
import type { SetupGraphNodeStatus, SetupRunAllStatus } from './setup-graph'
import type { MobileAppStoreSubmissionStatus } from './index'
import type { SetupLastRunSchema } from './setup-last-run'

type SetupLastRunState = z.infer<typeof SetupLastRunSchema.shape.state>

/**
 * A setup step's last run, as the Android and Apple checklists poll it.
 *
 * `starting` MUST be `active`. It is the state a dispatch sits in before the runner reports
 * anything. On 2026-09-22 the checklist pollers watched only `running`, so a row created as
 * `starting` never triggered a refetch, and the dashboard showed "Starting" for a run that had
 * already finished in the database. The run was fine; only the reading was stuck.
 */
export const setupLastRunLifecycle = defineRunLifecycle<SetupLastRunState>({
  phases: {
    starting: 'active',
    running: 'active',
    done: 'settled',
    needs_attention: 'settled',
    failed: 'settled'
  }
})

/**
 * A setup-graph node. Only `running` moves on its own; every other unfinished node waits on a
 * drive, a dependency or a person. Retry is offered on `failed` and `stuck`.
 */
export const setupGraphNodeLifecycle = defineRunLifecycle<SetupGraphNodeStatus>({
  phases: {
    done: 'settled',
    // Done at an older revision: it satisfies dependents and waits for an upgrade, not for a drive.
    upgradeable: 'settled',
    skipped: 'settled',
    running: 'active',
    ready: 'resting',
    queued: 'resting',
    waiting: 'resting',
    blocked: 'resting',
    failed: 'resting',
    stuck: 'resting',
    gated: 'resting'
  },
  retryable: ['failed', 'stuck']
})

/** A node that cannot proceed without a person. */
export function setupNodeNeedsOperator(status: SetupGraphNodeStatus): boolean {
  return status === 'failed' || status === 'blocked' || status === 'stuck'
}

/**
 * A run-full-setup drive. `stuck` is still running (a node is past its time limit), so it stays
 * `active`. `paused` is open but no backend holds its lease: nothing moves until one resumes it.
 */
export const setupRunAllLifecycle = defineRunLifecycle<SetupRunAllStatus>({
  phases: {
    running: 'active',
    stuck: 'active',
    paused: 'resting',
    failed: 'settled',
    complete: 'settled'
  }
})

/** A Play store submission. */
export const storeSubmissionLifecycle = defineRunLifecycle<MobileAppStoreSubmissionStatus>({
  phases: {
    QUEUED: 'active',
    DOWNLOADING: 'active',
    UPLOADING: 'active',
    COMMITTING: 'active',
    SUCCEEDED: 'settled',
    FAILED: 'settled'
  }
})

const EXPO_FAILED_STATUSES: ReadonlySet<string> = new Set(['errored', 'canceled', 'cancelled'])

/**
 * How an EAS build or submission ended, from Expo's raw status: `succeeded` only for `finished`,
 * `failed` for errored or cancelled, and `active` for anything still moving or never seen. Matched
 * case-insensitively for the reason `expoActivityPhase` gives. The phase says WHETHER a row is
 * done; this says how, which is what decides whether a build can be submitted.
 */
export function expoActivityOutcome(rawStatus: string): 'active' | 'succeeded' | 'failed' {
  const status = rawStatus.toLowerCase()
  if (status === 'finished') return 'succeeded'
  return EXPO_FAILED_STATUSES.has(status) ? 'failed' : 'active'
}

/**
 * An EAS build or submission, from Expo's raw status. Matched case-insensitively, because Expo
 * shouts (`IN_PROGRESS`) and the response deliberately keeps its strings rather than an enum, so
 * this is a known table plus a fallback rather than a `RunLifecycle`.
 *
 * Expo's moving statuses (new, in_queue, in_progress, awaiting_build, pending_cancel) and any status
 * we have never seen are all `active`. An unknown status counting as moving is the safe way round:
 * it polls a little longer, rather than declaring finished a row that is not.
 */
export function expoActivityPhase(rawStatus: string): RunPhase {
  return expoActivityOutcome(rawStatus) === 'active' ? 'active' : 'settled'
}
